import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { useRazorpay, RazorpayOrderOptions } from 'react-razorpay';

const Checkout = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { error, isLoading, Razorpay } = useRazorpay();
  const [cart, setCart] = useState(null);
  const [products, setProducts] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [processing, setProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
      
      const productIds = response.data.items.map(item => item.product_id);
      const productPromises = productIds.map(id => axios.get(`${API}/products/${id}`));
      const productResponses = await Promise.all(productPromises);
      
      const productsMap = {};
      productResponses.forEach(res => {
        productsMap[res.data.id] = res.data;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart');
    }
  };

  const calculateTotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const orderItems = cart.items.map(item => ({
        product_id: item.product_id,
        name: products[item.product_id]?.name || '',
        quantity: item.quantity,
        price: item.price
      }));

      const orderData = {
        items: orderItems,
        total_amount: calculateTotal(),
        payment_method: paymentMethod,
        shipping_address: shippingAddress
      };

      const response = await axios.post(`${API}/orders/create`, orderData);

      if (paymentMethod === 'online') {
        // Razorpay payment
        const options = {
          key: 'rzp_live_RUp3j7gnvlYLx0',
          amount: response.data.total_amount * 100,
          currency: 'INR',
          name: 'Happy Star',
          description: 'Order Payment',
          order_id: response.data.razorpay_order_id,
          handler: async (razorpayResponse) => {
            try {
              await axios.post(`${API}/orders/verify-payment`, {
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature
              });
              toast.success('Payment successful!');
              navigate('/dashboard');
            } catch (error) {
              console.error('Payment verification failed:', error);
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            name: shippingAddress.name,
            contact: shippingAddress.phone
          },
          theme: {
            color: '#f472b6'
          }
        };

        const razorpayInstance = new Razorpay(options);
        razorpayInstance.open();
      } else {
        // COD
        toast.success('Order placed successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order');
    } finally {
      setProcessing(false);
    }
  };

  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl text-gray-600 mb-4">Your cart is empty</p>
          <Button onClick={() => navigate('/products')} className="btn-primary text-white">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8" data-testid="checkout-heading">Checkout</h1>

        <form onSubmit={handlePlaceOrder}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Shipping & Payment */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="shipping-form">
                <h2 className="text-2xl font-bold mb-6">Shipping Address</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      required
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      required
                      value={shippingAddress.address}
                      onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      required
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      required
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      data-testid="state-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      required
                      value={shippingAddress.pincode}
                      onChange={(e) => setShippingAddress({...shippingAddress, pincode: e.target.value})}
                      data-testid="pincode-input"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="payment-method">
                <h2 className="text-2xl font-bold mb-6">Payment Method</h2>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="flex items-center space-x-2 mb-4 p-4 border-2 rounded-xl hover:border-pink-500 cursor-pointer">
                    <RadioGroupItem value="online" id="online" data-testid="payment-online" />
                    <Label htmlFor="online" className="cursor-pointer flex-1">
                      <div>
                        <p className="font-semibold">Online Payment (Razorpay)</p>
                        <p className="text-sm text-gray-600">Pay securely via credit/debit card, UPI, netbanking</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-4 border-2 rounded-xl hover:border-pink-500 cursor-pointer">
                    <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" />
                    <Label htmlFor="cod" className="cursor-pointer flex-1">
                      <div>
                        <p className="font-semibold">Cash on Delivery</p>
                        <p className="text-sm text-gray-600">Pay when you receive your order</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24" data-testid="order-summary">
                <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                  {cart.items.map((item) => {
                    const product = products[item.product_id];
                    if (!product) return null;

                    return (
                      <div key={item.product_id} className="flex justify-between text-sm">
                        <span className="text-gray-600">{product.name} x {item.quantity}</span>
                        <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t pt-4 space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold text-green-600">Free</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-pink-600" data-testid="checkout-total">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={processing}
                  className="w-full btn-primary text-white py-6 text-lg rounded-full"
                  data-testid="place-order-btn"
                >
                  {processing ? 'Processing...' : 'Place Order'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;